interface CacheItem<T> {
  value: T
  expiry: number
}

export const TTL = {
  MINUTE: 60, // 1 minute
  FIVE_MINUTES: 300, // 5 minutes
  TEN_MINUTES: 600, // 10 minutes
  HOUR: 3600, // 1 hour
  DAY: 86400, // 1 day
  NEVER: 0, // Never expire
} as const

// Use global scope for persistence in serverless environment
declare global {
  var _cache: Cache | undefined
  var _store: Map<string, CacheItem<any>> | undefined
}

class Cache {
  private readonly defaultTTL: number

  constructor(defaultTTL = TTL.FIVE_MINUTES) {
    if (global._cache) {
      return global._cache
    }

    // Initialize or reuse the global store
    if (!global._store) {
      global._store = new Map()
    }

    this.defaultTTL = defaultTTL * 1000 // Convert to milliseconds
    global._cache = this
  }

  set<T>(key: string, value: T, ttl?: number): void {
    const expiry = Date.now() + (ttl ? ttl * 1000 : this.defaultTTL)
    global._store?.set(key, { value, expiry })
    this.debug() // Log cache contents after each set
  }

  get<T>(key: string): T | null {
    const item = global._store?.get(key)
    if (!item) {
      console.log('Cache MISS (not found):', key)
      return null
    }

    if (Date.now() > item.expiry) {
      console.log('Cache MISS (expired):', key)
      global._store?.delete(key)
      return null
    }

    return item.value as T
  }

  delete(key: string): void {
    console.log('Cache DELETE:', key)
    global._store?.delete(key)
  }

  // Delete cache entries for a wallet address based on type
  deleteForWallet(
    walletAddress: string,
    type?: 'account' | 'privy' | 'profile' | 'rules'
  ): void {
    console.log(
      'Cache DELETE for wallet:',
      walletAddress,
      type ? `(type: ${type})` : '(all)'
    )
    const lowerWallet = walletAddress.toLowerCase()
    if (global._store) {
      for (const key of global._store.keys()) {
        // If type is specified, only delete cache entries of that type
        if (type) {
          if (key.startsWith(`${type}:${lowerWallet}`)) {
            global._store.delete(key)
          }
        } else {
          // If no type specified, delete all cache entries for the wallet
          if (key.includes(lowerWallet)) {
            global._store.delete(key)
          }
        }
      }
    }
  }

  clear(): void {
    console.log('Cache CLEAR')
    global._store?.clear()
  }

  // Debug method to see cache contents
  debug(): void {
    console.log('Cache contents:')
    if (global._store) {
      for (const [key, item] of global._store.entries()) {
        console.log(
          `- ${key}: ${JSON.stringify(item.value, null, 2)} expires in ${Math.round(
            (item.expiry - Date.now()) / 1000
          )}s`
        )
      }
    } else {
      console.log('No cache store found')
    }
  }
}

// Create a single wallet data cache instance
export const walletCache = new Cache(TTL.FIVE_MINUTES) // 5 minutes

// Cache keys for different data types
export const CACHE_KEYS = {
  PROFILE: (wallet: string) => `profile:${wallet.toLowerCase()}`,
  PRIVY: (wallet: string) => `privy:${wallet.toLowerCase()}`,
  ACCOUNT: (wallet: string) => `account:${wallet.toLowerCase()}`,
  RULES: (wallet: string) => `rules:${wallet.toLowerCase()}`,
  REFER_LIST: (inviteCode: string) => `refer-list:${inviteCode}`,
}

export default {
  walletCache,
  CACHE_KEYS,
  TTL,
}
